import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQuery } from '@tanstack/react-query';
import {
  WELCOME_DEFAULT_SECTIONS,
  WELCOME_SECTION_TYPES,
  WelcomePageContentSchema,
  collectWelcomeMediaIds,
  newWelcomeSection,
  type WelcomePageContent,
  type WelcomeSection,
  type WelcomeSectionType,
} from '@utazzo/shared';
import { UzButton, UzConfirmDialog, UzDrawer, UzErrorState, UzSkeleton, cn } from '@utazzo/ui';
import { ExternalLink, Plus, RotateCcw, Save, Undo2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  WELCOME_SECTION_META,
  WelcomeSectionForm,
  cleanSection,
} from '../components/section-forms';
import { SectionRow } from '../components/SectionRow';
import { WelcomeContent } from '../components/WelcomeContent';
import { useSaveWelcomePage, useWelcomePage } from '../welcome.api';

import { resolveMediaBatch } from '@/features/website-cms/components/media.api';

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

type Confirm = { kind: 'discard' } | { kind: 'restore' } | { kind: 'delete'; id: string };

/**
 * Editor for the Welcome Utazzer page (gated by web.welcome) - built like the Website CMS
 * page editor: an ordered, draggable list of sections (add / duplicate /
 * hide / delete) with the selected section's form beside it. Edits a local
 * draft and saves the whole page in one PUT; "Preview" renders the draft
 * with the exact components the live page uses.
 */
export const WelcomeEditorPage = (): React.JSX.Element => {
  const { data, isLoading, isError, refetch } = useWelcomePage();
  const save = useSaveWelcomePage();
  const [draft, setDraft] = useState<WelcomePageContent | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data.content);
  }, [data, draft]);

  const sections = draft?.sections ?? [];
  // The section whose accordion is open (none on first load, like the CMS editor).
  const active = sections.find((s) => s.id === activeId) ?? null;

  const dirty = useMemo(
    () => !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(data.content),
    [data, draft],
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Images uploaded in this session aren't in the server's media map yet -
  // resolve them for the preview only.
  const missingIds = useMemo(
    () => (draft && data ? collectWelcomeMediaIds(draft).filter((id) => !data.media[id]) : []),
    [draft, data],
  );
  const extraMedia = useQuery({
    queryKey: ['welcome-page', 'preview-media', missingIds],
    queryFn: () => resolveMediaBatch(missingIds),
    enabled: preview && missingIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const media = useMemo(() => {
    const m: Record<string, string> = { ...(data?.media ?? {}) };
    for (const r of extraMedia.data ?? []) m[r.id] = r.url;
    return m;
  }, [data, extraMedia.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (isLoading || (data && !draft)) {
    return (
      <div className="space-y-4">
        <UzSkeleton className="h-10 w-72" />
        <UzSkeleton className="h-96 w-full" />
      </div>
    );
  }
  if (isError || !data || !draft) {
    return <UzErrorState title="Couldn’t load the welcome page" onRetry={() => void refetch()} />;
  }

  const setSections = (fn: (prev: WelcomeSection[]) => WelcomeSection[]) =>
    setDraft((d) => (d ? { ...d, sections: fn(d.sections) } : d));

  const replace = (next: WelcomeSection) =>
    setSections((prev) => prev.map((s) => (s.id === next.id ? next : s)));

  const onDragEnd = (e: DragEndEvent): void => {
    const { active: a, over } = e;
    if (!over || a.id === over.id) return;
    setSections((prev) => {
      const from = prev.findIndex((s) => s.id === a.id);
      const to = prev.findIndex((s) => s.id === over.id);
      return from < 0 || to < 0 ? prev : arrayMove(prev, from, to);
    });
  };

  const addSection = (type: WelcomeSectionType) => {
    const s = newWelcomeSection(type, newId());
    // Insert right after the selected section so it lands where the admin is working.
    setSections((prev) => {
      const at = active ? prev.findIndex((x) => x.id === active.id) + 1 : prev.length;
      return [...prev.slice(0, at), s, ...prev.slice(at)];
    });
    setActiveId(s.id);
    setAdding(false);
    setPreview(false);
  };

  const duplicate = (s: WelcomeSection) => {
    const copy = { ...structuredClone(s), id: newId() };
    setSections((prev) => {
      const at = prev.findIndex((x) => x.id === s.id) + 1;
      return [...prev.slice(0, at), copy, ...prev.slice(at)];
    });
    setActiveId(copy.id);
  };

  const onSave = () => {
    const parsed = WelcomePageContentSchema.safeParse({ sections: sections.map(cleanSection) });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const idx = issue?.path[1];
      const bad = typeof idx === 'number' ? sections[idx] : undefined;
      if (bad) {
        setActiveId(bad.id);
        setPreview(false);
      }
      const where = bad ? WELCOME_SECTION_META[bad.type].label : 'Content';
      toast.error(`${where}: ${issue?.message ?? 'invalid value'}`);
      return;
    }
    save.mutate(parsed.data, { onSuccess: (res) => setDraft(res.content) });
  };

  const onConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === 'discard') setDraft(data.content);
    if (confirm.kind === 'restore') {
      setDraft({ sections: structuredClone(WELCOME_DEFAULT_SECTIONS) });
      setActiveId(null);
    }
    if (confirm.kind === 'delete') setSections((prev) => prev.filter((s) => s.id !== confirm.id));
    setConfirm(null);
  };

  const tabBtn = (on: boolean) =>
    cn(
      'rounded px-3 py-1 text-xs font-medium transition-colors',
      on ? 'bg-(--bg) text-(--fg) shadow-sm' : 'text-(--fg-faint) hover:text-(--fg)',
    );

  return (
    <div className="mx-auto w-full max-w-5xl">
      {/* Editor header - same shape as the Website CMS page editor's. */}
      <div className="flex flex-col gap-3 rounded-t-lg border border-(--border) bg-(--bg) px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="min-w-0">
          <h1 className="font-heading text-lg font-semibold text-(--fg)">Welcome Utazzer page</h1>
          <p className="text-xs text-(--fg-faint)">
            {data.isDefault
              ? 'Default design - not saved yet. Shown to every staff member after sign-in.'
              : `Shown to every staff member after sign-in · Last saved ${new Date(
                  data.updatedAt ?? '',
                ).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dirty && (
            <span className="text-xs font-medium text-(--ds-warning,#b45309)">Unsaved changes</span>
          )}
          <UzButton asChild variant="ghost" size="sm">
            <Link to="/welcome" className="gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" />
              Open live page
            </Link>
          </UzButton>
          <UzButton
            variant="outline"
            size="sm"
            leftIcon={<Undo2 className="h-4 w-4" />}
            disabled={!dirty || save.isPending}
            onClick={() => setConfirm({ kind: 'discard' })}
          >
            Discard
          </UzButton>
          <UzButton
            size="sm"
            leftIcon={<Save className="h-4 w-4" />}
            loading={save.isPending}
            disabled={!dirty}
            onClick={onSave}
          >
            Save
          </UzButton>
        </div>
      </div>

      <div className="rounded-b-lg border border-t-0 border-(--border) bg-(--bg)">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-(--border) px-3 py-2.5 sm:px-4">
          <div className="flex rounded-md bg-(--z-100) p-0.5">
            <button type="button" className={tabBtn(!preview)} onClick={() => setPreview(false)}>
              Sections ({sections.length})
            </button>
            <button type="button" className={tabBtn(preview)} onClick={() => setPreview(true)}>
              Preview
            </button>
          </div>
          {!preview && (
            <div className="flex items-center gap-2">
              <UzButton
                size="sm"
                variant="ghost"
                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                onClick={() => setConfirm({ kind: 'restore' })}
              >
                <span className="hidden sm:inline">Restore default</span>
                <span className="sm:hidden">Default</span>
              </UzButton>
              <UzButton
                size="sm"
                variant="secondary"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                disabled={sections.length >= 40}
                onClick={() => setAdding(true)}
              >
                Add section
              </UzButton>
            </div>
          )}
        </div>

        {preview ? (
          <div className="bg-[#F2F2F2] p-3 sm:p-6">
            <div className="mx-auto max-w-5xl">
              <WelcomeContent content={draft} websiteStars={data.websiteStars} media={media} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-2 py-3 sm:px-4 sm:py-4">
            {sections.length === 0 && (
              <p className="rounded-md border border-dashed border-(--border-strong) p-6 text-center text-sm text-(--fg-faint)">
                No sections yet. Use “Add section” to compose this page.
              </p>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={sections.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map((s, i) => (
                  <SectionRow
                    key={s.id}
                    section={s}
                    index={i}
                    open={active?.id === s.id}
                    onToggleOpen={() => setActiveId(active?.id === s.id ? null : s.id)}
                    onToggleVisible={() => replace({ ...s, visible: !s.visible })}
                    onDuplicate={() => duplicate(s)}
                    onDelete={() => setConfirm({ kind: 'delete', id: s.id })}
                  >
                    <WelcomeSectionForm section={s} onChange={replace} />
                  </SectionRow>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <UzDrawer
        open={adding}
        onOpenChange={setAdding}
        title="Add a section"
        description="Added below the open section (or at the end). Any type can be used more than once."
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {WELCOME_SECTION_TYPES.map((type) => {
            const meta = WELCOME_SECTION_META[type];
            const Icon = meta.icon;
            return (
              <button
                key={type}
                type="button"
                onClick={() => addSection(type)}
                className="flex items-start gap-3 rounded-lg border border-(--border) bg-(--bg) p-3 text-left transition-colors hover:border-(--border-strong) hover:bg-(--z-50)"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-(--z-100) text-(--fg-muted)">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-(--fg)">{meta.label}</span>
                  <span className="text-[12px] leading-tight text-(--fg-faint)">{meta.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </UzDrawer>

      <UzConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={
          confirm?.kind === 'delete'
            ? 'Delete this section?'
            : confirm?.kind === 'restore'
              ? 'Restore the default design?'
              : 'Discard unsaved changes?'
        }
        description={
          confirm?.kind === 'delete'
            ? 'It is removed from the draft; nothing changes for staff until you save.'
            : confirm?.kind === 'restore'
              ? 'Replaces every section with the original design copy. Nothing changes for staff until you save.'
              : 'The page goes back to the last saved version.'
        }
        confirmLabel={
          confirm?.kind === 'restore'
            ? 'Restore'
            : confirm?.kind === 'delete'
              ? 'Delete'
              : 'Discard'
        }
        tone="destructive"
        onConfirm={onConfirm}
      />
    </div>
  );
};
